'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Heart, ArrowRight, MapPin, Car } from 'lucide-react';
import Image from 'next/image';
import type { School } from '@/data/schools';
import { khdaBannerColor } from '@/data/schools';
import { getClosestCommunitiesWithPrices, formatCommuteTime } from '@/data/communityPrices';
import { estimateTravelMinutes } from '@/data/schools';
import { toSlug } from '@/lib/seo/slugs';

interface SchoolCardProps {
  school: School;
  communityFilter: string;
  compareList: string[];
  onToggleCompare: (id: string) => void;
  onSave: (id: string) => void;
  savedList: string[];
}

function formatFee(n: number) {
  if (n === 0) return 'N/A';
  return 'AED ' + n.toLocaleString('en-AE');
}

function SchoolWatermark({ color = '#1e3a5f' }: { color?: string }) {
  return (
    <svg
      viewBox="0 0 160 130"
      className="absolute right-0 bottom-0 h-full w-auto pointer-events-none select-none"
      style={{ opacity: 0.12 }}
      fill={color}
      aria-hidden="true"
    >
      <rect x="20" y="55" width="120" height="75" rx="3" />
      <rect x="55" y="30" width="50" height="50" rx="2" />
      <polygon points="80,5 45,35 115,35" />
      <rect x="78" y="2" width="3" height="18" />
      <polygon points="81,2 100,8 81,14" />
      <rect x="28" y="70" width="18" height="22" rx="2" fill="white" opacity="0.6" />
      <rect x="28" y="100" width="18" height="20" rx="2" fill="white" opacity="0.6" />
      <rect x="114" y="70" width="18" height="22" rx="2" fill="white" opacity="0.6" />
      <rect x="114" y="100" width="18" height="20" rx="2" fill="white" opacity="0.6" />
      <rect x="65" y="90" width="30" height="40" rx="15 15 0 0" fill="white" opacity="0.6" />
      <rect x="68" y="38" width="24" height="20" rx="12 12 0 0" fill="white" opacity="0.6" />
    </svg>
  );
}

export default function SchoolCard({ school, communityFilter, compareList, onToggleCompare, onSave, savedList }: SchoolCardProps) {
  const [hovered, setHovered] = useState(false);
  const [showAllCommunities, setShowAllCommunities] = useState(false);
  const isComparing = compareList.includes(school.id);
  const isSaved = savedList.includes(school.id);

  const communityTravelMinsRaw = communityFilter ? estimateTravelMinutes(communityFilter, school) : null;
  const communityTravelMins = communityTravelMinsRaw !== null ? formatCommuteTime(communityTravelMinsRaw) : null;
  const bannerColor = khdaBannerColor(school.khdaRating);

  // Desktop: 3 communities. Mobile handled separately.
  const closestCommunities = getClosestCommunitiesWithPrices(school.coordinates.lat, school.coordinates.lng, 3);
  const allCommunities = getClosestCommunitiesWithPrices(school.coordinates.lat, school.coordinates.lng, 10);

  const khdaColor = bannerColor;

  return (
    <>
      {/* ============================================================
          DESKTOP / TABLET CARD (sm and above)
          Restored to pre-last-two-prompts state
      ============================================================ */}
      <div
        className={`hidden sm:flex group bg-white rounded-2xl shadow-sm transition-all duration-200 overflow-hidden flex-col ${
          hovered ? 'shadow-md -translate-y-0.5' : ''
        } ${isComparing ? 'ring-2 ring-primary/40' : ''}`}
        style={{ border: `2.5px solid ${bannerColor}` }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Banner */}
        <div className="relative overflow-hidden bg-white" style={{ minHeight: '120px', height: 'auto' }}>
          <svg
            className="absolute top-0 left-0 pointer-events-none select-none"
            style={{ width: '160px', height: '100%', zIndex: 1 }}
            preserveAspectRatio="none"
            viewBox="0 0 160 120"
            aria-hidden="true"
          >
            <polygon points="0,0 160,0 0,120" fill={bannerColor} />
          </svg>

          <div className="absolute z-10" style={{ top: '50%', left: '6px', transform: 'translateY(-50%)' }}>
            <Image
              src="/assets/images/image-1786254194769.png"
              alt="SchoolMapper"
              width={104}
              height={104}
              className="object-contain drop-shadow-md"
              style={{ width: '104px', height: '104px' }}
            />
          </div>

          <div
            className="relative z-10 flex flex-col justify-center"
            style={{ marginLeft: '130px', marginRight: '52px', paddingTop: '14px', paddingBottom: '14px', minHeight: '120px' }}
          >
            <h3
              className="font-bold leading-snug"
              style={{ color: '#1e2d5a', fontSize: 'clamp(13px, 1.4vw, 18px)', fontWeight: 700, whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'break-word', hyphens: 'none', overflow: 'visible' }}
            >
              {school.name}
            </h3>
            <p className="text-xs mt-1 font-semibold" style={{ color: bannerColor }}>{school.schoolType}</p>
          </div>

          <SchoolWatermark color={bannerColor} />

          <button
            onClick={() => onSave(school.id)}
            className={`absolute top-2 right-2 z-20 p-1.5 rounded-lg transition-all duration-150 active:scale-90 ${
              isSaved ? 'bg-red-500 text-white' : 'bg-white/80 text-muted-foreground hover:bg-white hover:text-red-500 border border-border'
            }`}
            title={isSaved ? 'Remove from saved' : 'Save school'}
          >
            <Heart size={13} className={isSaved ? 'fill-white' : ''} />
          </button>
        </div>

        {/* Card Body */}
        <div className="p-4 flex flex-col gap-3 flex-1">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-foreground">
              <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#1aaa6e" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 7 12 7s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
              <span className="text-muted-foreground font-medium">Location:</span>
              <span className="truncate">{school.location}</span>
            </div>

            {communityFilter && communityTravelMins !== null ? (
              <div className="flex items-center gap-2 text-xs text-foreground">
                <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#2563eb" aria-hidden="true"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
                <span className="text-muted-foreground font-medium">Commute Time:</span>
                <span className={`font-semibold ${communityTravelMins <= 15 ? 'text-emerald-600' : communityTravelMins <= 30 ? 'text-sky-600' : communityTravelMins <= 45 ? 'text-amber-600' : 'text-red-600'}`}>
                  ~{communityTravelMins} min peak from {communityFilter}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-foreground">
                <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#2563eb" aria-hidden="true"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
                <span className="text-muted-foreground font-medium">Commute Time:</span>
                <span className="text-muted-foreground">Select community to calculate</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-foreground">
              <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#1aaa6e" aria-hidden="true"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/></svg>
              <span className="text-muted-foreground font-medium">Fee Range:</span>
              <span className="font-semibold tabular-nums">
                {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-foreground">
              <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#6B21A8" aria-hidden="true"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>
              <span className="text-muted-foreground font-medium">Inspection Rating:</span>
              <span className="font-semibold" style={{ color: bannerColor }}>{school.khdaRating}</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-foreground">
              <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#2563eb" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v6a5 5 0 0 0 5 5h3v2H6V4h12v-2zM6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h14v-4h-3a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h3V2H6z"/><path d="M21 7h-6v2h6V7zm0 4h-6v2h6v-2z"/></svg>
              <span className="text-muted-foreground font-medium">Curriculum:</span>
              <span className="font-semibold">{school.curriculum}</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-foreground">
              <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#6B7280" aria-hidden="true"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
              <span className="text-muted-foreground font-medium">Age Range:</span>
              <span className="font-semibold">{school.ageRange}</span>
            </div>
          </div>

          {closestCommunities.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border-b border-emerald-100">
                <svg viewBox="0 0 24 24" className="w-3 h-3 text-emerald-600" fill="currentColor" aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                <span className="text-xs font-semibold text-foreground">Closest Communities</span>
              </div>
              <div className="divide-y divide-border/50">
                {closestCommunities.map((c) => (
                  <div key={c.name} className="px-3 py-1.5 flex items-center justify-between">
                    <span className="text-xs font-medium text-foreground truncate pr-2">{c.name}</span>
                    <span className={`text-xs font-bold tabular-nums shrink-0 ${c.travelMins <= 15 ? 'text-emerald-600' : c.travelMins <= 30 ? 'text-sky-600' : 'text-amber-600'}`}>~{formatCommuteTime(c.travelMins)} min</span>
                  </div>
                ))}
              </div>
              <Link
                href={`/school-profile-comparison?school=${school.id}&tab=location`}
                className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors border-t border-emerald-100"
                onClick={(e) => e.stopPropagation()}
              >
                View nearby communities
                <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          )}

          <div className="border border-border rounded-lg px-3 py-2.5 bg-muted/30">
            <p className="text-xs font-semibold text-foreground">Admissions Availability</p>
            <p className="text-xs text-muted-foreground mt-0.5">Unknown</p>
          </div>
        </div>

        {/* Card Footer — View Profile primary, Compare secondary */}
        <div className="px-4 pb-4 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Link
              href={`/school-profile-comparison?school=${school.id}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-all duration-150 active:scale-95 shadow-sm"
              style={{ backgroundColor: bannerColor }}
            >
              View Profile
              <ArrowRight size={14} />
            </Link>
            <button
              onClick={() => onToggleCompare(school.id)}
              className={`px-3 py-2.5 rounded-lg text-xs font-medium border transition-all duration-150 active:scale-95 ${
                isComparing ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-muted-foreground border-border hover:border-primary/30 hover:text-primary'
              }`}
              title={isComparing ? 'Remove from compare' : 'Add to compare'}
            >
              {isComparing ? '✓ Compare' : 'Compare'}
            </button>
            <button
              onClick={() => onSave(school.id)}
              className={`p-2.5 rounded-lg border transition-all duration-150 active:scale-95 ${
                isSaved ? 'bg-red-50 text-red-500 border-red-200' : 'bg-white text-muted-foreground border-border hover:border-red-200 hover:text-red-400'
              }`}
              title={isSaved ? 'Remove from shortlist' : 'Add to shortlist'}
            >
              <Heart size={13} className={isSaved ? 'fill-red-500' : ''} />
            </button>
          </div>
          <Link
            href={`/communities-near/${toSlug(school.name)}`}
            className="text-xs text-center text-muted-foreground hover:text-primary transition-colors"
          >
            Communities near {school.shortName} →
          </Link>
        </div>
      </div>

      {/* ============================================================
          MOBILE CARD (below sm — 320px to 430px)
          Premium single-column mobile design
      ============================================================ */}
      <div
        className={`sm:hidden bg-white rounded-2xl overflow-hidden flex flex-col shadow-sm transition-all duration-200 ${isComparing ? 'ring-2 ring-primary/40' : ''}`}
        style={{ border: `1.5px solid ${bannerColor}`, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
      >
        {/* Mobile Card Header */}
        <div className="px-3 pt-3 pb-3" style={{ boxSizing: 'border-box' }}>
          <div className="flex items-start gap-2" style={{ width: '100%', minWidth: 0 }}>
            {/* School Logo */}
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
              style={{ backgroundColor: bannerColor + '18', border: `1.5px solid ${bannerColor}40` }}
            >
              <Image
                src="/assets/images/image-1786254194769.png"
                alt="SchoolMapper"
                width={36}
                height={36}
                className="object-contain"
                style={{ width: '36px', height: '36px' }}
              />
            </div>

            {/* School Name + Type */}
            <div className="flex-1 min-w-0">
              <h3
                className="font-bold text-sm leading-tight"
                style={{ color: '#1e2d5a', wordBreak: 'break-word', overflowWrap: 'break-word' }}
              >
                {school.name}
              </h3>
              <p className="text-xs font-semibold mt-0.5" style={{ color: bannerColor }}>{school.schoolType}</p>
            </div>

            {/* Heart / Shortlist */}
            <button
              onClick={() => onSave(school.id)}
              className={`p-2 rounded-xl transition-all duration-150 active:scale-90 shrink-0 ${
                isSaved ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground hover:text-red-500'
              }`}
              title={isSaved ? 'Remove from shortlist' : 'Add to shortlist'}
            >
              <Heart size={15} className={isSaved ? 'fill-white' : ''} />
            </button>
          </div>

          {/* Key Info Pills */}
          <div className="mt-2.5 flex flex-wrap gap-1" style={{ width: '100%' }}>
            {/* KHDA Rating */}
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
              style={{ backgroundColor: bannerColor + '18', color: bannerColor, border: `1px solid ${bannerColor}40` }}
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="currentColor" aria-hidden="true"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
              {school.khdaRating}
            </span>
            {/* Curriculum */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
              {school.curriculum}
            </span>
            {/* Age Range */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
              {school.ageRange}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-3 border-t border-border/60" />

        {/* Key Details */}
        <div className="px-3 py-2.5 space-y-1.5" style={{ boxSizing: 'border-box', width: '100%' }}>
          {/* Location */}
          <div className="flex items-start gap-2" style={{ minWidth: 0 }}>
            <MapPin size={13} className="text-primary shrink-0 mt-0.5" />
            <span className="text-xs text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: 0, flex: 1 }}>{school.location}</span>
          </div>

          {/* Fee Range — prominent */}
          <div className="flex items-start gap-2" style={{ minWidth: 0 }}>
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0 mt-0.5" fill="#1aaa6e" aria-hidden="true"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/></svg>
            <span className="text-xs font-bold text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: 0, flex: 1 }}>
              {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
            </span>
          </div>

          {/* Commute */}
          {communityFilter && communityTravelMins !== null ? (
            <div className="flex items-start gap-2" style={{ minWidth: 0 }}>
              <Car size={13} className="text-blue-500 shrink-0 mt-0.5" />
              <span className={`text-xs font-semibold ${communityTravelMins <= 15 ? 'text-emerald-600' : communityTravelMins <= 30 ? 'text-sky-600' : communityTravelMins <= 45 ? 'text-amber-600' : 'text-red-600'}`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: 0, flex: 1 }}>
                ~{communityTravelMins} min from {communityFilter}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
              <Car size={13} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground" style={{ minWidth: 0 }}>Select a community to calculate commute</span>
            </div>
          )}
        </div>

        {/* Closest Communities — compact expandable */}
        {allCommunities.length > 0 && (
          <div className="mx-3 mb-3 rounded-xl border border-border/60 overflow-hidden bg-sky-50/50" style={{ boxSizing: 'border-box', width: 'calc(100% - 24px)' }}>
            <div className="px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" className="w-3 h-3 text-sky-600 shrink-0" fill="currentColor" aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                <span className="text-xs font-semibold text-foreground">Closest Communities</span>
              </div>
            </div>
            <div className="divide-y divide-border/40">
              {(showAllCommunities ? allCommunities : allCommunities.slice(0, 2)).map((c) => (
                <div key={c.name} className="px-3 py-1.5 flex items-center justify-between bg-white" style={{ minWidth: 0 }}>
                  <span className="text-xs text-foreground min-w-0 flex-1 pr-2" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{c.name}</span>
                  <span className={`text-xs font-bold shrink-0 ${c.travelMins <= 15 ? 'text-emerald-600' : c.travelMins <= 30 ? 'text-sky-600' : 'text-amber-600'}`}>~{formatCommuteTime(c.travelMins)} min</span>
                </div>
              ))}
            </div>
            {allCommunities.length > 2 && (
              <button
                onClick={() => setShowAllCommunities(!showAllCommunities)}
                className="w-full px-3 py-2 text-xs font-semibold text-primary text-center hover:bg-sky-100 transition-colors"
              >
                {showAllCommunities ? 'Show fewer' : `View more nearby communities`}
              </button>
            )}
          </div>
        )}

        {/* Mobile Card Actions */}
        <div className="px-3 pb-3 flex items-center gap-2" style={{ boxSizing: 'border-box', width: '100%' }}>
          <Link
            href={`/school-profile-comparison?school=${school.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-all duration-150 active:scale-95"
            style={{ backgroundColor: bannerColor, minWidth: 0 }}
          >
            View school
          </Link>
          <button
            onClick={() => onToggleCompare(school.id)}
            className={`px-3 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-150 active:scale-95 shrink-0 ${
              isComparing ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-muted-foreground border-border hover:border-primary/30 hover:text-primary'
            }`}
          >
            {isComparing ? '✓' : 'Shortlist'}
          </button>
        </div>
      </div>
    </>
  );
}